import { useState } from "react";
import { Activity, AlertCircle, MessageSquare, Users, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCopilotAnalytics } from "@/hooks/useCopilotAnalytics";
import { CopilotToolTable } from "./CopilotToolTable";

const WINDOWS = [7, 30, 90];

export function CopilotAnalytics() {
  const [days, setDays] = useState(30);
  const { data, isLoading, isError, error } = useCopilotAnalytics(days);

  const failureRate =
    data && data.totalToolCalls > 0 ? data.totalToolFailures / data.totalToolCalls : 0;
  const peakDay = data?.daily.reduce(
    (max, d) => (d.userMessages > (max?.userMessages ?? 0) ? d : max),
    data.daily[0],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Copilot Analytics</h2>
          <p className="text-sm text-muted-foreground">
            Derived from stored chat history — no separate tracking.
          </p>
        </div>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <Button
              key={w}
              variant={days === w ? "default" : "outline"}
              size="sm"
              onClick={() => setDays(w)}
            >
              {w}d
            </Button>
          ))}
        </div>
      </div>

      {isError && (
        <Card className="border-destructive/40">
          <CardContent className="flex items-center gap-2 py-6 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Could not load analytics: {error instanceof Error ? error.message : "unknown error"}
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {WINDOWS.concat(0).map((k) => (
            <Skeleton key={k} className="h-24 rounded-xl" />
          ))}
        </div>
      )}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={<Users className="h-4 w-4" />}
              label="Active users"
              value={data.totalActiveUsers}
              hint={`asked something in ${days}d`}
            />
            <StatCard
              icon={<MessageSquare className="h-4 w-4" />}
              label="Messages sent"
              value={data.totalUserMessages}
              hint={peakDay ? `peak ${peakDay.userMessages} on ${peakDay.day}` : undefined}
            />
            <StatCard
              icon={<Wrench className="h-4 w-4" />}
              label="Tool calls"
              value={data.totalToolCalls}
              hint={`${data.tools.length} distinct tools`}
            />
            <StatCard
              icon={<Activity className="h-4 w-4" />}
              label="Failure rate"
              value={`${Math.round(failureRate * 100)}%`}
              hint={`${data.totalToolFailures} failed calls`}
              alerting={failureRate >= 0.2}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tool reliability</CardTitle>
              <CardDescription>
                A tool the model never calls means its description needs work; a tool that
                fails often means the tool itself does.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CopilotToolTable tools={data.tools} />
            </CardContent>
          </Card>

          {data.errors.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top errors</CardTitle>
                <CardDescription>What tools returned when they failed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.errors.map((e) => (
                  <div
                    key={e.error}
                    className="flex items-start justify-between gap-4 border-b pb-2 text-sm last:border-0 last:pb-0"
                  >
                    <span className="font-mono text-xs break-all">{e.error}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {e.count}×
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: string;
  alerting?: boolean;
}

function StatCard({ icon, label, value, hint, alerting }: StatCardProps) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {icon}
          {label}
        </div>
        <p
          className={`mt-2 text-2xl font-semibold tabular-nums ${
            alerting ? "text-destructive" : ""
          }`}
        >
          {value}
        </p>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}
